/** Calorie goals use percentage-based pacing, a short-term 7,700 kcal/kg approximation, and intake safety limits. */
import {
  ABSOLUTE_MAX_KCAL,
  ABSOLUTE_MIN_KCAL,
  AGGRESSION_WEEKLY_PERCENT_GAIN,
  AGGRESSION_WEEKLY_PERCENT_LOSE,
  ALGO_VERSIONS,
  LOW_KCAL_BMR_FRACTION_FLAG,
  MAX_SURPLUS_FRACTION_GAIN,
  MAX_WEEKLY_PERCENT_LOSE,
  MIN_KCAL_BMR_FRACTION_LOSE,
  NEAR_GOAL_KG,
  NEAR_GOAL_MAX_WEEKLY_PERCENT,
  TISSUE_KCAL_PER_KG,
} from "./constants";
import { calculateBmi, calculateBmr } from "./metabolism";
import {
  ENGINE_VERSION,
  type CalorieTargetValue,
  type EngineMeta,
  type EngineProfile,
  type EngineResult,
} from "./types";

export type WeeklyRateResult = {
  readonly requestedKg: number;
  readonly cappedKg: number;
  readonly reasons: readonly string[];
};

function roundOne(value: number): number {
  return Math.round(value * 10) / 10;
}

function roundTwo(value: number): number {
  return Math.round(value * 100) / 100;
}

function pacePercent(rates: Record<string, number>, pace: string | undefined): number {
  return rates[pace ?? "steady"] ?? rates.steady ?? 0;
}

function createMeta(
  now: number,
  restricted: boolean,
  inputs: EngineMeta["inputs"],
): EngineMeta {
  return {
    kind: "calorieTarget",
    engineVersion: ENGINE_VERSION,
    algorithmVersion: ALGO_VERSIONS.calorieTarget,
    computedAt: now,
    estimateKind: "initial",
    confidence: restricted ? "low" : "moderate",
    explanation: restricted
      ? "Automated calorie adjustment is restricted for safety, so maintenance intake is shown instead; seek professional guidance for an individual plan."
      : "The target converts the requested weekly rate with 7,700 kcal/kg as a short-term approximation, then applies intake safety limits.",
    inputs,
  };
}

export function weeklyRateFromAggression(
  goalType: string | undefined,
  pace: string | undefined,
  weightKg: number,
  targetWeightKg?: number,
  bmi?: number,
): WeeklyRateResult {
  if (goalType === "maintain" || goalType === "healthier") {
    return { requestedKg: 0, cappedKg: 0, reasons: [] };
  }
  if (goalType === "gain" || goalType === "muscle") {
    const requestedKg = (weightKg * pacePercent(AGGRESSION_WEEKLY_PERCENT_GAIN, pace)) / 100;
    return { requestedKg: roundTwo(requestedKg), cappedKg: roundTwo(requestedKg), reasons: [] };
  }
  if (goalType !== "lose") return { requestedKg: 0, cappedKg: 0, reasons: [] };

  const requestedMagnitude =
    (weightKg * pacePercent(AGGRESSION_WEEKLY_PERCENT_LOSE, pace)) / 100;
  const absoluteCap = (weightKg * MAX_WEEKLY_PERCENT_LOSE) / 100;
  let cappedMagnitude = Math.min(requestedMagnitude, absoluteCap);
  const reasons: string[] = [];
  if (cappedMagnitude < requestedMagnitude) reasons.push("Rate capped at the maximum automated weekly loss percentage.");

  const nearGoal =
    targetWeightKg !== undefined && Math.abs(weightKg - targetWeightKg) <= NEAR_GOAL_KG;
  const lowerBmi = bmi !== undefined && bmi < 22;
  if (nearGoal || lowerBmi) {
    const nearGoalCap = (weightKg * NEAR_GOAL_MAX_WEEKLY_PERCENT) / 100;
    if (cappedMagnitude > nearGoalCap) cappedMagnitude = nearGoalCap;
    if (nearGoal) reasons.push("Rate reduced because the target is near.");
    if (lowerBmi) reasons.push("Rate reduced because BMI is below 22.");
  }

  return {
    requestedKg: -roundTwo(requestedMagnitude),
    cappedKg: -roundTwo(cappedMagnitude),
    reasons,
  };
}

export function calculateCalorieTarget(
  profile: EngineProfile,
  expenditureKcal: number,
  now = Date.now(),
): EngineResult<CalorieTargetValue> {
  const expenditure = Math.round(expenditureKcal);
  const bmi = calculateBmi(profile, now)?.value.bmi;
  const bmr = calculateBmr(profile, now)?.value.bmrKcal;
  const safetyFlags = profile.safetyFlags ?? [];
  const underweightLoss = profile.goalType === "lose" && bmi !== undefined && bmi < 18.5;
  if (safetyFlags.length > 0 || underweightLoss) {
    const constraintReasons = [
      ...safetyFlags,
      ...(underweightLoss ? ["Underweight BMI blocks automated weight-loss targets"] : []),
    ];
    return {
      value: {
        targetKcal: expenditure,
        adjustmentKcal: 0,
        weeklyChangeKg: 0,
        weeklyPercentOfBodyWeight: 0,
        wasConstrained: true,
        constraintReasons,
        restricted: true,
      },
      meta: createMeta(now, true, {
        expenditureKcal: expenditure,
        goalType: profile.goalType ?? "unknown",
        bmi: bmi ?? null,
        safetyFlagCount: safetyFlags.length,
      }),
    };
  }

  const weightKg = profile.weightKg ?? 70;
  const rate = weeklyRateFromAggression(
    profile.goalType,
    profile.weightLossPace,
    weightKg,
    profile.targetWeightKg,
    bmi,
  );
  const requestedAdjustment = (rate.cappedKg * TISSUE_KCAL_PER_KG) / 7;
  let targetKcal = Math.round((expenditure + requestedAdjustment) / 10) * 10;
  const constraintReasons = [...rate.reasons];
  let wasConstrained = rate.reasons.length > 0;

  if (profile.goalType === "lose") {
    const intakeFloor = Math.max(
      ABSOLUTE_MIN_KCAL,
      bmr === undefined ? ABSOLUTE_MIN_KCAL : MIN_KCAL_BMR_FRACTION_LOSE * bmr,
    );
    if (targetKcal < intakeFloor) {
      targetKcal = Math.round(intakeFloor);
      wasConstrained = true;
      constraintReasons.push("Deficit reduced to keep intake safe — timeline extended");
    }
  }

  if (profile.goalType === "gain" || profile.goalType === "muscle") {
    const surplusCap = expenditure * MAX_SURPLUS_FRACTION_GAIN;
    if (targetKcal > surplusCap) {
      targetKcal = Math.round(surplusCap);
      wasConstrained = true;
      constraintReasons.push("Surplus capped at 25% of estimated expenditure.");
    }
  }

  const clampedTarget = Math.min(ABSOLUTE_MAX_KCAL, Math.max(ABSOLUTE_MIN_KCAL, targetKcal));
  if (clampedTarget !== targetKcal) {
    wasConstrained = true;
    constraintReasons.push(
      clampedTarget === ABSOLUTE_MIN_KCAL
        ? "Target raised to the absolute minimum calorie limit."
        : "Target reduced to the absolute maximum calorie limit.",
    );
    targetKcal = clampedTarget;
  }
  if (bmr !== undefined && targetKcal < LOW_KCAL_BMR_FRACTION_FLAG * bmr) {
    constraintReasons.push("Target is unusually low relative to estimated BMR.");
  }

  const adjustmentKcal = targetKcal - expenditure;
  const weeklyChangeKg = roundOne((adjustmentKcal * 7) / TISSUE_KCAL_PER_KG);
  const weeklyPercentOfBodyWeight =
    weightKg > 0 ? roundOne((weeklyChangeKg / weightKg) * 100) : 0;

  return {
    value: {
      targetKcal,
      adjustmentKcal,
      weeklyChangeKg,
      weeklyPercentOfBodyWeight,
      wasConstrained,
      constraintReasons,
      restricted: false,
    },
    meta: createMeta(now, false, {
      expenditureKcal: expenditure,
      bmrKcal: bmr ?? null,
      weightKg: roundOne(weightKg),
      targetWeightKg: profile.targetWeightKg ?? null,
      goalType: profile.goalType ?? "unknown",
      pace: profile.weightLossPace ?? "steady",
      bmi: bmi ?? null,
    }),
  };
}
